/**
 * Locale-preserve safety net.
 *
 * Goal: even if a hardcoded <a href="/foo"> sneaks past the server-side
 * `loc-url` snippet wrapping, the client intercepts the click and
 * rewrites the href to include the current locale before navigation.
 *
 * Also persists the customer's chosen locale to localStorage so:
 *   1. A direct visit to the canonical "/" later (e.g. from a bookmark
 *      or a poorly-localized inbound link) can redirect to the stored
 *      locale.
 *   2. Any future client-side route changes can read it.
 *
 * Behavior:
 *   - Reads current locale from <html lang>. If primary locale or no
 *     lang attribute, this script is a no-op (zero overhead in English).
 *   - Stores locale in localStorage as `lt-locale`.
 *   - Capture-phase click listener on document — intercepts any
 *     <a href> click whose href is a same-origin internal path missing
 *     the locale prefix, and rewrites the href right before navigation
 *     proceeds.
 *   - Does NOT touch: external URLs (http://, https://, //), anchors
 *     (#fragment), mailto:, tel:, javascript:, shopify://, links
 *     already prefixed with the locale.
 *
 * Edge cases:
 *   - <a target="_blank">: still rewritten before browser opens new tab.
 *   - Middle-click / cmd-click: native browser handler reads the href
 *     attribute — since we rewrite it synchronously, those navigations
 *     also pick up the prefix.
 *   - JS-driven navigation (location.assign, etc.) is NOT covered —
 *     would need a separate hook. The server-side snippet should still
 *     cover those callers; this script is the chrome safety net.
 */
(function() {
  'use strict';

  var html = document.documentElement;
  var locale = (html.getAttribute('lang') || '').trim().toLowerCase();
  if (!locale) return;

  // Detect primary locale — Shopify renders <html lang="en"> when on
  // the default locale. We could also read window.Shopify.locale but
  // the lang attribute is more portable.
  // For this store the primary locale is English ('en'). Anything else
  // → we're on a secondary locale that needs preservation.
  var primary = (locale === 'en');

  // Persist locale for future visits/sessions
  if (!primary) {
    try { localStorage.setItem('lt-locale', locale); } catch (e) {}
  } else {
    // Primary locale (EN). Customer has navigated to the EN site — either
    // by clicking the EN language coin, by visiting an EN URL directly,
    // or via a forwarded EN link. Clear any stale 'lt-locale=ar' from a
    // previous AR session so the click/MutationObserver rewriting below
    // is genuinely a no-op, and internal link navigation (cart, product
    // cards, predictive search results) doesn't get silently coerced
    // back to /ar/.
    try { localStorage.removeItem('lt-locale'); } catch (e) {}
  }

  if (primary) return; // No interception needed in English

  var prefix = '/' + locale + '/';
  var prefixNoSlash = '/' + locale;

  function needsLocalePrefix(href) {
    if (!href) return false;
    // Skip anchors
    if (href.charAt(0) === '#') return false;
    // Skip protocol-relative + absolute external URLs
    if (href.indexOf('://') !== -1 || href.indexOf('//') === 0) return false;
    // Skip non-navigation schemes
    if (/^(mailto:|tel:|javascript:|sms:)/i.test(href)) return false;
    // Skip Shopify resolution placeholders (shouldn't appear in href but defensive)
    if (href.indexOf('shopify://') === 0) return false;
    // Must start with / to be an internal absolute path
    if (href.charAt(0) !== '/') return false;
    // Already prefixed?
    if (href === prefixNoSlash || href.indexOf(prefix) === 0) return false;
    return true;
  }

  function prefixHref(href) {
    // "/" → "/ar/"; "/foo" → "/ar/foo"; "/foo?q=x" → "/ar/foo?q=x"
    if (href === '/') return prefixNoSlash + '/';
    return prefixNoSlash + href;
  }

  // Capture-phase listener so we rewrite BEFORE the browser navigates
  document.addEventListener('click', function(e) {
    var link = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!link) return;
    var href = link.getAttribute('href');
    if (!needsLocalePrefix(href)) return;
    var fixed = prefixHref(href);
    link.setAttribute('href', fixed);
    // The native browser handler reads the just-set href and navigates correctly
  }, true);

  // Also rewrite any same-document mutation by MutationObserver — this is
  // useful for AJAX-loaded snippets (cart drawer recommendations, predictive
  // search results) that may inject <a> with hardcoded paths.
  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue; // element only
          var links = node.matches && node.matches('a[href]') ? [node] : (node.querySelectorAll ? node.querySelectorAll('a[href]') : []);
          for (var k = 0; k < links.length; k++) {
            var l = links[k];
            var href = l.getAttribute('href');
            if (needsLocalePrefix(href)) {
              l.setAttribute('href', prefixHref(href));
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
